import React from 'react';
import { useTheme } from '../../../context/ThemeContext';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { SkeletonProfileContent } from '../../common/LoadingSkeleton';
import ForumActivity from './ForumActivity';

/**
 * UserProfileContent component for displaying user's detailed information
 * Shows professional information, company details, location, and forum activity in organized cards
 * Responsive design optimized for mobile and desktop viewing
 */
const UserProfileContent = ({ profileData, loading = false }) => {
  const { isDarkMode } = useTheme();

  // Extract user data with fallbacks
  const {
    currentRole = '',
    companyName = '',
    location = '',
    userId = '',
    email = ''
  } = profileData?.basicInfo || {};

  if (loading) {
    return <SkeletonProfileContent />;
  }

  return (
    <div className="space-y-6">
      {/* Professional and Location Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Professional Information Card */}
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6`}>
        <div className="flex items-center mb-4">
          <BuildingOffice2Icon className="w-6 h-6 mr-3 text-purple-600 dark:text-purple-400" />
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
            Professional Information
          </h2>
        </div>

        <div className="space-y-4">
          {/* Current Role */}
          {currentRole ? (
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Current Role
              </dt>
              <dd className="text-base text-gray-900 dark:text-white">
                {currentRole}
              </dd>
            </div>
          ) : (
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Current Role
              </dt>
              <dd className="text-base text-gray-400 dark:text-gray-500 italic">
                Not specified
              </dd>
            </div>
          )}

          {/* Company */}
          {companyName ? (
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Company
              </dt>
              <dd className="text-base text-gray-900 dark:text-white flex items-center">
                <svg 
                  className="w-4 h-4 mr-2 text-gray-400" 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                >
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                </svg>
                {companyName}
              </dd>
            </div>
          ) : (
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Company
              </dt>
              <dd className="text-base text-gray-400 dark:text-gray-500 italic">
                Not specified
              </dd>
            </div>
          )}

          {/* Email */}
          {email ? (
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Email
              </dt>
              <dd className="text-base text-gray-900 dark:text-white">
                <a 
                  href={`mailto:${email}`}
                  className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300
                           hover:underline transition-colors duration-200 ease-out
                           focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 
                           dark:focus:ring-offset-gray-800 rounded px-1 -mx-1
                           flex items-center"
                >
                  <svg 
                    className="w-4 h-4 mr-2 text-gray-400" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {email}
                </a>
              </dd>
            </div>
          ) : (
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Email
              </dt>
              <dd className="text-base text-gray-400 dark:text-gray-500 italic">
                Not provided
              </dd>
            </div>
          )}
        </div>
      </div>

      {/* Location Card */}
      {location ? (
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6`}>
          <div className="flex items-center mb-4">
            <svg 
              className="w-6 h-6 mr-3 text-purple-600 dark:text-purple-400" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
              Location
            </h2>
          </div>

          <div className="flex items-center">
            <svg 
              className="w-4 h-4 mr-2 text-gray-400" 
              fill="currentColor" 
              viewBox="0 0 20 20"
            >
              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
            <span className="text-base text-gray-900 dark:text-white">
              {location}
            </span>
          </div>
        </div>
      ) : (
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6`}>
          <div className="flex items-center mb-4">
            <svg 
              className="w-6 h-6 mr-3 text-purple-600 dark:text-purple-400" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
              Location
            </h2>
          </div>

          <div className="flex items-center">
            <span className="text-base text-gray-400 dark:text-gray-500 italic">
              Location not specified
            </span>
          </div>
        </div>
      )}
      </div>

      {/* Forum Activity Section */}
      <ForumActivity 
        userId={userId || profileData?.uid || profileData?.userId} 
        className="col-span-full"
      />
    </div>
  );
};

export default UserProfileContent;